import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import WarningIcon from '@material-ui/icons/Warning';
import ErrorIcon from '@material-ui/icons/Error';
import React from 'react';
import PanoramaFishEyeIcon from '@material-ui/icons/PanoramaFishEye';
import {
  InstanceFields,
  ValidateZeroTrustFields,
} from 'modules/environments/interfaces';
import { connect } from 'react-redux';
import ApplicationState from 'store/interfaces';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { validateZeroTrustRequest } from 'modules/environments/actions';
import i18n from 'i18n';
import clsx from 'clsx';
import styles from '../styles';

const useStyles = makeStyles(styles);

export interface Props {
  instance: InstanceFields;
  environmentUuid: string;
  isLoading: boolean;
  validateZeroTrust: ValidateZeroTrustFields;
  validateZeroTrustRequest: (props) => void;
}

const ValidateZeroTrust = ({
  instance,
  environmentUuid,
  isLoading,
  validateZeroTrust,
  validateZeroTrustRequest,
}: Props) => {
  const classes = useStyles();

  const compareVersions = (
    currentVersion: string,
    minimumVersion: string
  ): number => {
    const current = currentVersion?.split('.').map(Number);
    const minimum = minimumVersion?.split('.').map(Number);

    for (let i = 0; i < Math.max(current.length, minimum.length); i += 1) {
      const currentVal = current[i] || 0;
      const minimumVal = minimum[i] || 0;

      if (currentVal > minimumVal) return 1;
      if (currentVal < minimumVal) return -1;
    }
    return 0;
  };

  React.useEffect(() => {
    validateZeroTrustRequest({
      environmentUuid,
      instanceUuid: instance?.instance_uuid,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [environmentUuid, instance]);

  const renderZeroTrustStatus = () => {
    if (
      validateZeroTrust?.environment_config?.enabled &&
      validateZeroTrust?.integration_test?.valid &&
      (validateZeroTrust?.provider_rule?.valid ||
        !validateZeroTrust?.provider_rule?.applicable) &&
      validateZeroTrust?.platform_version?.valid
    ) {
      return (
        <Grid item className={classes.zeroTrustStatusValid}>
          <CheckCircleIcon className={classes.statusZeroTrustIconValid} />
          <Typography className={classes.validZeroTrust}>
            {i18n.t('VALID_ZERO_TRUST_KEY')}
          </Typography>
        </Grid>
      );
    }

    if (!validateZeroTrust?.integration_test?.valid) {
      return (
        <Grid item className={classes.zeroTrustStatusInvalid}>
          <ErrorIcon className={classes.statusZeroTrustIconInvalid} />
          <Typography className={classes.invalidZeroTrust}>
            {i18n.t('INVALID_ZERO_TRUST_KEY')}
          </Typography>
        </Grid>
      );
    }

    if (
      validateZeroTrust?.integration_test?.valid &&
      (!validateZeroTrust?.environment_config.enabled ||
        (validateZeroTrust?.provider_rule?.applicable &&
          !validateZeroTrust?.provider_rule?.valid) ||
        !validateZeroTrust?.platform_version?.valid)
    ) {
      return (
        <Grid item className={classes.zeroTrustStatusWarning}>
          <WarningIcon className={classes.statusZeroTrustIconWarning} />
          <Typography className={classes.warningZeroTrust}>
            {i18n.t('INCONSISTENT_ZERO_TRUST_KEY')}
          </Typography>
        </Grid>
      );
    }

    return <Grid></Grid>;
  };

  return (
    <Grid container>
      {isLoading ? (
        <Grid container justify="center" className={classes.isLoading}>
          <CircularProgress size={45} />
        </Grid>
      ) : (
        <>
          <Grid container justify="center" className={classes.renderStatus}>
            {renderZeroTrustStatus()}
          </Grid>
          <Grid container className={classes.containerInfoZeroTrust}>
            <Grid item className={classes.itemInfoZeroTrust}>
              <Typography
                component="div"
                className={classes.infoZeroTrustTitle}
              >
                {validateZeroTrust?.environment_config?.enabled ? (
                  <CheckCircleIcon
                    className={classes.statusZeroTrustIconValid}
                  />
                ) : (
                  <ErrorIcon className={classes.statusZeroTrustIconInvalid} />
                )}
                {`${i18n.t('ZERO_TRUST_CONFIG_AT_ENV_KEY')}`}
                <Box
                  display="flex"
                  alignItems="center"
                  className={clsx({
                    [classes.trueOption]:
                      validateZeroTrust?.environment_config?.enabled,
                    [classes.falseOption]: !validateZeroTrust
                      ?.environment_config?.enabled,
                  })}
                ></Box>
              </Typography>
            </Grid>
            <Grid item className={classes.itemInfoZeroTrust}>
              {validateZeroTrust?.provider_rule?.applicable ? (
                <Typography
                  component="div"
                  className={classes.infoZeroTrustTitle}
                >
                  {validateZeroTrust?.provider_rule?.valid ? (
                    <CheckCircleIcon
                      className={classes.statusZeroTrustIconValid}
                    />
                  ) : (
                    <ErrorIcon className={classes.statusZeroTrustIconInvalid} />
                  )}
                  {`${i18n.t('PROVIDER_RULE_KEY')}`}
                  <Box
                    display="flex"
                    alignItems="center"
                    className={clsx({
                      [classes.trueOption]:
                        validateZeroTrust?.provider_rule?.valid,
                      [classes.falseOption]: !validateZeroTrust?.provider_rule
                        ?.valid,
                    })}
                  ></Box>
                </Typography>
              ) : (
                <Grid item className={classes.itemInfoZeroTrustPlatform}>
                  <Typography
                    component="div"
                    className={classes.infoZeroTrustTitle}
                  >
                    <PanoramaFishEyeIcon
                      className={classes.statusZeroTrustIconNotApplicable}
                    />
                    {`${i18n.t('PROVIDER_RULE_KEY')}`}
                    <Box
                      display="flex"
                      alignItems="center"
                      className={clsx({
                        [classes.trueOption]:
                          validateZeroTrust?.provider_rule?.valid,
                        [classes.falseOption]: !validateZeroTrust?.provider_rule
                          ?.valid,
                      })}
                    ></Box>
                  </Typography>
                  <Grid item className={classes.itemInfoPlatformVersion}>
                    <Typography
                      component="div"
                      className={classes.infoZeroTrustTitle}
                    >
                      {`${i18n.t('ENVIRONMENT_NOT_ELIGIBLE_FOR_TEST_KEY')}: `}
                      <Box
                        display="flex"
                        alignItems="center"
                        className={classes.blueOption}
                      >
                        {'Multicloud'}
                      </Box>
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Grid>
            <Grid item className={classes.itemInfoZeroTrustPlatform}>
              <Typography
                component="div"
                className={classes.infoZeroTrustTitle}
              >
                {validateZeroTrust?.integration_test?.valid ? (
                  <CheckCircleIcon
                    className={classes.statusZeroTrustIconValid}
                  />
                ) : (
                  <ErrorIcon className={classes.statusZeroTrustIconInvalid} />
                )}
                {`${i18n.t('INTEGRATION_TEST_KEY')}`}
                <Box
                  display="flex"
                  alignItems="center"
                  className={clsx({
                    [classes.trueOption]:
                      validateZeroTrust?.integration_test?.valid,
                    [classes.falseOption]: !validateZeroTrust?.integration_test
                      ?.valid,
                  })}
                ></Box>
              </Typography>
              {!validateZeroTrust?.integration_test?.valid && (
                <Grid item className={classes.itemInfoPlatformVersion}>
                  <Typography
                    component="div"
                    className={classes.infoZeroTrustTitle}
                  >
                    {`${i18n.t('DETAILS_KEY')}: `}
                    <Box
                      display="flex"
                      alignItems="center"
                      className={classes.blueOption}
                    >
                      {validateZeroTrust?.integration_test?.error || '-'}
                    </Box>
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Grid item className={classes.itemInfoZeroTrustPlatform}>
              <Typography
                component="div"
                className={classes.infoZeroTrustTitle}
              >
                {validateZeroTrust?.platform_version?.valid ? (
                  <CheckCircleIcon
                    className={classes.statusZeroTrustIconValid}
                  />
                ) : (
                  <ErrorIcon className={classes.statusZeroTrustIconInvalid} />
                )}
                {`${i18n.t('PLATFORM_VERSION_KEY')}`}
                <Box
                  display="flex"
                  alignItems="center"
                  className={clsx({
                    [classes.trueOption]:
                      validateZeroTrust?.platform_version?.valid,
                    [classes.falseOption]: !validateZeroTrust?.platform_version
                      ?.valid,
                  })}
                ></Box>
              </Typography>
              <Grid item className={classes.itemInfoPlatformVersion}>
                <Typography
                  component="div"
                  className={classes.infoZeroTrustTitle}
                >
                  {`${i18n.t('CURRENT_VERSION_KEY')}: `}
                  <Box
                    display="flex"
                    alignItems="center"
                    className={clsx({
                      [classes.trueOption]:
                        compareVersions(
                          validateZeroTrust?.platform_version
                            ?.current_version || '',
                          validateZeroTrust?.platform_version
                            ?.minimum_version || ''
                        ) >= 0,
                      [classes.falseOption]:
                        compareVersions(
                          validateZeroTrust?.platform_version
                            ?.current_version || '',
                          validateZeroTrust?.platform_version
                            ?.minimum_version || ''
                        ) < 0,
                      [classes.blueOption]: !validateZeroTrust?.platform_version
                        ?.current_version,
                    })}
                  >
                    {validateZeroTrust?.platform_version?.current_version ||
                      i18n.t('INFO_NOT_AVAILABLE_KEY')}
                  </Box>
                </Typography>
                <Typography
                  component="div"
                  className={classes.infoZeroTrustTitle}
                >
                  {`${i18n.t('MINIMUM_VERSION_KEY')}: `}
                  <Box
                    display="flex"
                    alignItems="center"
                    className={classes.blueOption}
                  >
                    {validateZeroTrust?.platform_version?.minimum_version ||
                      '-'}
                  </Box>
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  );
};

const mapStateToProps = ({
  environments: { validateZeroTrust, isLoading },
}: ApplicationState) => ({
  validateZeroTrust,
  isLoading,
});

export default connect(mapStateToProps, {
  validateZeroTrustRequest,
})(ValidateZeroTrust);
